<template>
  <div class="prospective-ai-editor">
    <!-- Slim header -->
    <div class="editor-header">
      <div class="header-title">
        <font-awesome-icon :icon="['fas', 'pen']" class="title-icon" />
        <span>AI Detection Editor</span>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="header-btn"
          :disabled="!canUndo || isSaving"
          title="Undo (Ctrl+Z)"
          @click.prevent="undo"
        >
          <font-awesome-icon :icon="['fas', 'undo']" />
        </button>
        <button
          type="button"
          class="header-btn"
          :disabled="!canRedo || isSaving"
          title="Redo (Ctrl+Shift+Z)"
          @click.prevent="redo"
        >
          <font-awesome-icon :icon="['fas', 'redo']" />
        </button>
        <div v-if="auditChanged" class="change-indicator">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
          <span>Unsaved changes</span>
        </div>
      </div>
    </div>

    <div class="editor-body">
      <div class="canvas-stage" ref="stageRef">
        <div v-if="imageUrl" class="image-container">
          <img
            ref="imageRef"
            :src="imageUrl"
            class="editor-image"
            :style="imageFilterStyle"
            @load="onImageLoad"
          />
          <canvas
            v-show="imageReady"
            ref="canvasRef"
            class="editor-canvas"
            @mousedown="handleMouseDown"
            @mousemove="handleHover"
            @mouseleave="handleMouseLeave"
            @dblclick="handleDoubleClick"
          />
        </div>
        <div v-else class="no-image">
          <font-awesome-icon :icon="['fas', 'image']" size="3x" />
          <p>No image available</p>
        </div>

        <div
          v-if="hintVisible"
          class="canvas-hint"
          :style="hintStyle"
          @mousedown="startHintDrag"
        >
          <span class="hint-drag" title="Drag to move">
            <font-awesome-icon :icon="['fas', 'grip-lines']" />
          </span>
          <div class="hint-text">
            <template v-if="isAdding">
              Click and drag on the image to draw a {{ drawTypeLabel }}.
            </template>
            <template
              v-else-if="editedAnnotation && editedAnnotation.type === 'freehand'"
            >
              Hover to reveal a point, then drag to move it. Double-click an edge
              to add a point, Alt/Shift-click a point to remove it. Drag the
              finding to reposition.
            </template>
            <template v-else>
              Drag the handles to resize, or drag the finding to reposition. Click
              the check icon on the detection to finish.
            </template>
          </div>
          <button
            type="button"
            class="hint-close"
            title="Dismiss"
            @click.prevent="dismissHint"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>

      <aside class="detections-panel">
        <!-- Detections list -->
        <div class="panel-section detections-section">
          <div class="panel-header">
            <h4>Detections ({{ visibleDetections.length }})</h4>
            <span v-if="deletedCount > 0" class="deleted-count">
              {{ deletedCount }} removed
            </span>
          </div>
          <div class="panel-list">
            <DetectionItem
              v-for="detection in visibleDetections"
              :key="detection.id"
              :detection="detection"
              :is-editing="editingAnnotationId === detection.id"
              @edit="toggleEdit"
              @delete="deleteDetection"
              @cancel-edit="cancelEditSession"
            />
            <p v-if="visibleDetections.length === 0" class="empty-list">
              No detections. Add one below.
            </p>
          </div>
        </div>

        <!-- Add pathology section -->
        <div class="panel-section add-section">
          <PathologySelector
            v-model:selected-id="selectedPathologyId"
            v-model:other-name="otherName"
            :pathologies="availablePathologies"
            :disabled="isAdding"
          />
          <button
            type="button"
            class="add-btn"
            :class="{ active: isAdding }"
            :disabled="!isAdding && !canAdd"
            @click.prevent="toggleAddMode"
          >
            <font-awesome-icon :icon="['fas', isAdding ? 'times' : 'plus']" />
            <span>{{ isAdding ? "Cancel Adding" : "Add Pathology" }}</span>
            <span class="draw-type-badge">
              {{ drawType === "box" ? "Box" : "Freehand" }}
            </span>
          </button>
        </div>

        <!-- Save / Cancel -->
        <div class="panel-section action-section">
          <button
            type="button"
            class="action-btn cancel-btn"
            :disabled="isSaving"
            title="Close editor (discards unsaved edits)"
            @click.prevent="handleCancel"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
            <span>Close</span>
          </button>
          <button
            type="button"
            class="action-btn save-btn"
            :disabled="!auditChanged || isSaving"
            title="Save changes"
            @click.prevent="handleSave"
          >
            <font-awesome-icon
              :icon="['fas', isSaving ? 'spinner' : 'save']"
              :spin="isSaving"
            />
            <span>{{ isSaving ? "Saving..." : "Save Changes" }}</span>
          </button>
        </div>
      </aside>
    </div>

    <!-- Unsaved-changes warning when cancelling -->
    <BModal
      v-model="showCancelWarning"
      title="Discard all changes?"
      ok-title="Discard & Exit"
      cancel-title="Keep Editing"
      @ok="confirmCancel"
    >
      <p>
        All edits you've made to this image will be lost and the editor will
        close. Use Undo (Ctrl+Z) to revert a single edit.
      </p>
    </BModal>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { CanvasDrawingManager } from "@/helpers/canvasDrawingManager";
import { PathologySelector, DetectionItem } from "./EditorControls";

const props = defineProps({
  imageSrc: {
    type: String,
    default: null,
  },
  submission: {
    type: Object,
    default: null,
  },
  aiPathologies: {
    type: Array,
    default: () => [],
  },
  project: {
    type: Object,
    default: null,
  },
  // Optional image enhancement filter style (passed through from parent)
  imageFilterStyle: {
    type: String,
    default: "",
  },
  // Whether to draw detection text labels (mirrors the read-only overlay toggle
  // so the same control affects both renderers).
  showLabels: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["save", "cancel"]);

const EXCLUDED_DIAGNOSES = ["cardio", "thoracic"];
const OTHER_LABEL = "other";

// Fallback colour palette (mirrors parent). AI detections usually already carry
// a colour assigned by the parent computed, so this is only a safety net.
const COLOURS = [
  "#C77D3D", "#A4414A", "#7D1E5F", "#5C0F6B", "#3C0A6F", "#1E0A6E",
  "#0B0C63", "#0A1052", "#0C1441", "#13192F", "#1D1E1E", "#2A2213",
  "#3A2709", "#4B2C00", "#5D3100", "#6F3600", "#813B00", "#933F00",
  "#A54200", "#B74400",
];

// ---- Refs / DOM ----
const stageRef = ref(null);
const imageRef = ref(null);
const canvasRef = ref(null);

// ---- Editor state ----
const editorMode = ref("view"); // 'view' | 'add'
const selectedPathologyId = ref(null);
const otherName = ref("");
const editingAnnotationId = ref(null);
// Index of the freehand vertex currently revealed (hovered / dragged). Freehand
// vertices are invisible by default and only this one is drawn.
const highlightedAnchorIndex = ref(null);
const isSaving = ref(false);
const showCancelWarning = ref(false);

// ---- Instruction hint card (draggable + dismissible) ----
const hintDismissed = ref(false);
// Position within the canvas stage, in px. null = default (bottom-centre via CSS).
const hintPos = ref(null);
let hintDrag = null;

// ---- Annotations ----
// Single source of truth. Original AI detections have isOriginal=true and keep
// a reference to their original ai object. Newly added ones have isOriginal=false.
const annotations = ref([]);
// Original AI ids that the user removed (sent to feedback as concordance=false).
const deletedIds = ref(new Set());
// Cardio/Thoracic are not editable; passed through unchanged on save.
const cardioThoracic = ref([]);
// Every original AI pathology id (incl. cardio/thoracic) for post-refresh matching.
const allOriginalIds = ref([]);

// ---- Canvas / Drawing ----
const drawingManager = ref(null);
const resizeObserver = ref(null);
const imageReady = ref(false);
const originalWidth = ref(0);
const originalHeight = ref(0);
const canvasWidth = ref(0);
const canvasHeight = ref(0);

// Drag/draw tracking
let currentAnchor = null;

// ---- Computed ----
const imageUrl = computed(() => props.imageSrc);
const visibleDetections = computed(() => annotations.value);
// Annotations with geometry that can be drawn on the canvas. Diagnosis-only
// detections (no coordinates) are excluded from rendering.
const drawableAnnotations = computed(() =>
  annotations.value.filter((a) => !a.isDiagnosis),
);
// The annotation currently being focus-edited (if any).
const editedAnnotation = computed(() =>
  annotations.value.find((a) => a.id === editingAnnotationId.value),
);
const deletedCount = computed(() => deletedIds.value.size);

// Dirty flag is derived purely from the undo history pointer: we're "changed"
// whenever we've stepped away from the clean baseline captured at load.
const auditChanged = computed(() => historyIndex.value > 0);
const canUndo = computed(() => {
  if (historyIndex.value <= 0) return false;
  // Focus mode: only enabled while the entry being undone touched this finding.
  const focusId = editingAnnotationId.value;
  if (!focusId) return true;
  return history.value[historyIndex.value].touchedId === focusId;
});
const canRedo = computed(() => {
  if (historyIndex.value >= history.value.length - 1) return false;
  const focusId = editingAnnotationId.value;
  if (!focusId) return true;
  return history.value[historyIndex.value + 1].touchedId === focusId;
});

// project.freehand determines whether new detections are freehand polygons or
// bounding boxes. Only one mode is ever available for a given project.
const drawType = computed(() =>
  props.project?.freehand ? "freehand" : "box"
);
const drawTypeLabel = computed(() =>
  drawType.value === "box" ? "bounding box" : "freehand polygon"
);
const isAdding = computed(() => editorMode.value === "add");

// Instruction hint is shown while adding or focus-editing, unless dismissed.
const hintVisible = computed(
  () => (isAdding.value || editingAnnotationId.value != null) && !hintDismissed.value,
);
const hintStyle = computed(() =>
  hintPos.value
    ? {
        left: `${hintPos.value.left}px`,
        top: `${hintPos.value.top}px`,
        right: "auto",
        bottom: "auto",
        transform: "none",
      }
    : {},
);

function dismissHint() {
  hintDismissed.value = true;
}

function startHintDrag(e) {
  // Let the dismiss button work without starting a drag.
  if (e.target.closest(".hint-close")) return;

  const stage = stageRef.value;
  const card = e.currentTarget;
  if (!stage || !card) return;
  e.preventDefault(); // avoid text selection while dragging

  const stageRect = stage.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  hintDrag = {
    stageRect,
    cardW: cardRect.width,
    cardH: cardRect.height,
    offsetX: e.clientX - cardRect.left,
    offsetY: e.clientY - cardRect.top,
  };
  window.addEventListener("mousemove", onHintDrag);
  window.addEventListener("mouseup", endHintDrag);
}

function onHintDrag(e) {
  if (!hintDrag) return;
  const { stageRect, cardW, cardH, offsetX, offsetY } = hintDrag;
  const left = Math.max(
    0,
    Math.min(e.clientX - stageRect.left - offsetX, stageRect.width - cardW),
  );
  const top = Math.max(
    0,
    Math.min(e.clientY - stageRect.top - offsetY, stageRect.height - cardH),
  );
  hintPos.value = { left, top };
}

function endHintDrag() {
  hintDrag = null;
  window.removeEventListener("mousemove", onHintDrag);
  window.removeEventListener("mouseup", endHintDrag);
}

// Re-show the hint whenever the add/edit context changes, so a freshly entered
// mode always shows its instructions even after a previous dismissal.
watch([isAdding, editingAnnotationId], () => {
  hintDismissed.value = false;
});

// The pathology list includes ALL project pathologies (not only primary ones).
const availablePathologies = computed(() => {
  if (!props.project?.pathologies) return [];
  return props.project.pathologies.map((p, index) => ({
    id: p._id,
    label: p.label,
    value: normalizeName(p.label),
    color: COLOURS[index % COLOURS.length],
  }));
});

const selectedPathology = computed(() =>
  availablePathologies.value.find((p) => p.id === selectedPathologyId.value)
);

const isOtherSelected = computed(() => {
  if (!selectedPathology.value) return false;
  return (
    (selectedPathology.value.label || "").trim().toLowerCase() === OTHER_LABEL
  );
});

// Drawing may start only when a valid pathology is resolved. For "Other" the
// user must also type a name, which becomes the stored predicted_diagnosis.
const canAdd = computed(() => {
  if (!selectedPathology.value) return false;
  if (isOtherSelected.value) return otherName.value.trim().length > 0;
  return true;
});

// ---- Helpers ----
function normalizeName(name) {
  return (name || "").replace(/[^A-Z0-9]+/gi, "").toLowerCase();
}

function formatLabel(name) {
  if (!name) return "";
  return name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// ---- Annotation bootstrap ----
function buildAnnotations() {
  const list = [];
  const passthrough = [];
  const originalIds = [];

  (props.aiPathologies || []).forEach((entry, index) => {
    const ai = entry?.ai;
    if (!ai || !ai._id) return;
    originalIds.push(ai._id);

    const diagnosis = (ai.predicted_diagnosis || "").toLowerCase();
    if (EXCLUDED_DIAGNOSES.includes(diagnosis)) {
      passthrough.push(ai);
      return;
    }

    const isDiagnosis = ai.isDiagnosis === true;
    const isFreehand = ai.freehandpoints && ai.freehandpoints.length > 1;
    const annotation = {
      id: ai._id,
      type: isDiagnosis ? "diagnosis" : isFreehand ? "freehand" : "box",
      pathology: formatLabel(ai.predicted_diagnosis),
      color: ai.colour || COLOURS[index % COLOURS.length],
      isOriginal: true,
      isDiagnosis,
      originalAi: ai,
    };

    if (isDiagnosis) {
      // No geometry - listed for delete-only, passed through unchanged on save.
    } else if (isFreehand) {
      annotation.freehandpoints = ai.freehandpoints;
    } else if (ai.bounding_box && ai.bounding_box.x !== undefined) {
      annotation.coords = { ...ai.bounding_box };
    } else {
      // No usable geometry; skip.
      return;
    }

    list.push(annotation);
  });

  annotations.value = list;
  cardioThoracic.value = passthrough;
  allOriginalIds.value = originalIds;
  deletedIds.value = new Set();
  editingAnnotationId.value = null;
  resetHistory();
}

// ---- History (undo / redo) ----
// Snapshot-based history over annotations + deletedIds. The baseline (index 0)
// is captured in buildAnnotations(); each committed edit pushes a new snapshot,
// so auditChanged is derived purely from the history pointer. Snapshots clone
// geometry only and preserve the originalAi reference (shared, never mutated).
const history = ref([]);
const historyIndex = ref(-1);
// Geometry snapshot of the finding currently being focus-edited, used by the
// per-finding "Cancel" (X) to revert only this edit session's changes.
const editSessionSnapshot = ref(null);

function cloneAnnotations(list) {
  return list.map((a) => ({
    ...a,
    coords: a.coords ? { ...a.coords } : a.coords,
    freehandpoints: a.freehandpoints
      ? a.freehandpoints.map((p) => ({ ...p }))
      : a.freehandpoints,
  }));
}

function annotationSignature(ann) {
  let geo = "none";
  if (ann.coords) {
    geo = `box:${ann.coords.x},${ann.coords.y},${ann.coords.w},${ann.coords.h}`;
  } else if (ann.freehandpoints) {
    geo = `fh:${ann.freehandpoints.map((p) => `${p.x},${p.y}`).join(";")}`;
  }
  return `${ann.id}|${ann.type}|${geo}|${ann.isOriginal ? 1 : 0}|${ann.isDiagnosis ? 1 : 0}|${ann.pathology || ""}|${ann.value || ""}`;
}

function snapshotsEqual(a, b) {
  if (a.annotations.length !== b.annotations.length) return false;
  for (let i = 0; i < a.annotations.length; i++) {
    if (
      annotationSignature(a.annotations[i]) !== annotationSignature(b.annotations[i])
    )
      return false;
  }
  if (a.deletedIds.size !== b.deletedIds.size) return false;
  for (const id of a.deletedIds) if (!b.deletedIds.has(id)) return false;
  return true;
}

function takeSnapshot() {
  return {
    annotations: cloneAnnotations(annotations.value),
    deletedIds: new Set(deletedIds.value),
  };
}

function restoreSnapshot(snap) {
  annotations.value = cloneAnnotations(snap.annotations);
  deletedIds.value = new Set(snap.deletedIds);
}

function resetHistory() {
  history.value = [{ snapshot: takeSnapshot(), touchedId: null }];
  historyIndex.value = 0;
}

// touchedId tags which finding an edit affected (null for the baseline, an add,
// or a delete). It bounds focus-mode undo/redo so only the visible, focused
// finding is ever reverted — entries from other findings act as a barrier.
function pushHistory(touchedId = null) {
  const candidate = takeSnapshot();
  const last = history.value[historyIndex.value];
  // Skip no-op commits (e.g. clicking a handle without dragging, or cancelling
  // an edit session that made no changes) so the dirty flag stays accurate.
  if (last && snapshotsEqual(last.snapshot, candidate)) return;
  history.value = history.value.slice(0, historyIndex.value + 1);
  history.value.push({ snapshot: candidate, touchedId });
  historyIndex.value = history.value.length - 1;
}

function undo() {
  const dm = drawingManager.value;
  // Never rewind mid-gesture; the in-flight state would desync from history.
  if (dm && (dm.isDraggingAnchor || dm.isMovingAnnotation || dm.isDrawing)) return;
  if (historyIndex.value <= 0) return;
  const focusId = editingAnnotationId.value;
  // In focus mode, only revert edits to the focused finding. The barrier is the
  // first entry that touched something else (another finding, an add, a delete),
  // so a finding hidden by focus mode is never changed invisibly.
  if (focusId && history.value[historyIndex.value].touchedId !== focusId) return;
  historyIndex.value -= 1;
  restoreSnapshot(history.value[historyIndex.value].snapshot);
  // Stay focused on the finding being edited unless this revert actually
  // removed it (e.g. it was added this session), so a single undo doesn't
  // boot the user out of an otherwise fine editing session.
  keepFocusIfStillPresent();
  highlightedAnchorIndex.value = null;
  renderAllAnnotations();
}

function redo() {
  const dm = drawingManager.value;
  if (dm && (dm.isDraggingAnchor || dm.isMovingAnnotation || dm.isDrawing)) return;
  if (historyIndex.value >= history.value.length - 1) return;
  const focusId = editingAnnotationId.value;
  if (focusId && history.value[historyIndex.value + 1].touchedId !== focusId)
    return;
  historyIndex.value += 1;
  restoreSnapshot(history.value[historyIndex.value].snapshot);
  keepFocusIfStillPresent();
  highlightedAnchorIndex.value = null;
  renderAllAnnotations();
}

// After a history jump, drop out of focus-edit mode only if the finding being
// edited is no longer present. The session snapshot is retained so the
// per-finding "Cancel" (X) still targets the original focus-enter geometry.
function keepFocusIfStillPresent() {
  if (!editingAnnotationId.value) return;
  const stillThere = annotations.value.some(
    (a) => a.id === editingAnnotationId.value,
  );
  if (!stillThere) {
    editingAnnotationId.value = null;
    editSessionSnapshot.value = null;
  }
}

// ---- Canvas dimension management ----
function recalculateDimensions() {
  const img = imageRef.value;
  const canvas = canvasRef.value;
  if (!img || !canvas || !img.naturalWidth) return;

  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const containerWidth = img.clientWidth;
  const containerHeight = img.clientHeight;
  if (!containerWidth || !containerHeight) return;

  const imageAspect = naturalWidth / naturalHeight;
  const containerAspect = containerWidth / containerHeight;

  let displayedWidth, displayedHeight;
  if (imageAspect > containerAspect) {
    displayedWidth = containerWidth;
    displayedHeight = containerWidth / imageAspect;
  } else {
    displayedHeight = containerHeight;
    displayedWidth = containerHeight * imageAspect;
  }

  const nextW = Math.round(displayedWidth);
  const nextH = Math.round(displayedHeight);

  if (
    nextW !== canvasWidth.value ||
    nextH !== canvasHeight.value ||
    naturalWidth !== originalWidth.value ||
    naturalHeight !== originalHeight.value
  ) {
    originalWidth.value = naturalWidth;
    originalHeight.value = naturalHeight;
    canvasWidth.value = nextW;
    canvasHeight.value = nextH;

    canvas.width = nextW;
    canvas.height = nextH;

    if (drawingManager.value) {
      drawingManager.value.updateDimensions(
        naturalWidth,
        naturalHeight,
        nextW,
        nextH
      );
    } else {
      drawingManager.value = new CanvasDrawingManager(
        canvas,
        canvas.getContext("2d"),
        naturalWidth,
        naturalHeight,
        nextW,
        nextH
      );
    }

    renderAllAnnotations();
  }
}

function setupResizeObserver() {
  if (!imageRef.value) return;
  if (resizeObserver.value) resizeObserver.value.disconnect();

  resizeObserver.value = new ResizeObserver(() => {
    recalculateDimensions();
  });
  resizeObserver.value.observe(imageRef.value);
}

function onImageLoad() {
  imageReady.value = true;
  nextTick(() => {
    recalculateDimensions();
    setupResizeObserver();
    renderAllAnnotations();
  });
}

// ---- Rendering ----
// When an annotation is being edited, only that one is rendered (with its anchor
// handles) so the user's field of view is unobstructed - mirroring the behaviour
// of drawing a new annotation, where only the in-progress shape is shown.
function renderAllAnnotations() {
  const dm = drawingManager.value;
  if (!dm) return;

  if (editingAnnotationId.value) {
    // Focus mode: render only the annotation being edited and its anchor
    // handles. The label is hidden so the area immediately around the ROI is
    // unobstructed; the edited finding is signalled by the gold card border.
    dm.renderAnnotations(
      drawableAnnotations.value,
      editingAnnotationId.value,
      false,
    );
    const editing = drawableAnnotations.value.find(
      (a) => a.id === editingAnnotationId.value
    );
    if (editing)
      dm.drawAnchors(editing, highlightedAnchorIndex.value);
  } else {
    dm.renderAnnotations(
      drawableAnnotations.value,
      null,
      props.showLabels,
    );
  }
}

// ---- Mode handling ----
function setMode(mode) {
  editorMode.value = mode;
  if (mode === "add") {
    // Adding new: stop editing existing.
    editingAnnotationId.value = null;
  }
  renderAllAnnotations();
}

function toggleAddMode() {
  if (isAdding.value) {
    setMode("view");
  } else if (canAdd.value) {
    setMode("add");
  }
}

// ---- Coordinate helper ----
function eventToPoint(e) {
  const rect = canvasRef.value.getBoundingClientRect();
  return {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  };
}

// ---- Mouse handlers ----
function handleMouseDown(e) {
  const dm = drawingManager.value;
  if (!dm) return;

  const point = eventToPoint(e);

  // Editing existing annotation via anchor drag.
  if (editorMode.value === "view" && editingAnnotationId.value) {
    const annotation = annotations.value.find(
      (a) => a.id === editingAnnotationId.value
    );
    if (annotation) {
      const anchor = dm.getAnchorAtPoint(point, annotation);
      if (anchor) {
        // Alt/Shift-click a freehand vertex to remove it (min 3 vertices).
        if (
          annotation.type === "freehand" &&
          (e.altKey || e.shiftKey) &&
          annotation.freehandpoints &&
          annotation.freehandpoints.length > 3
        ) {
          deleteFreehandVertex(annotation, anchor.index);
          e.preventDefault();
          return;
        }
        currentAnchor = anchor;
        dm.isDraggingAnchor = true;
        // Reveal the vertex being dragged so it stays visible during the drag,
        // and snapshot the geometry so neighbouring vertices follow smoothly.
        if (annotation.type === "freehand") {
          highlightedAnchorIndex.value = anchor.index;
          dm.startFreehandVertexDrag(annotation);
        }
        attachDragListeners();
        e.preventDefault();
        return;
      }

      // Click on the body / border / label of the annotation -> move it.
      if (dm.isPointInAnnotation(point, annotation)) {
        dm.startMove(point, annotation);
        attachDragListeners();
        e.preventDefault();
        return;
      }
    }
  }

  // Start drawing a new annotation (box or freehand, per project setting).
  if (editorMode.value === "add" && canAdd.value) {
    dm.startDrawing(point, drawType.value);
    attachDragListeners();
    e.preventDefault();
  }
}

function handleMouseMove(e) {
  const dm = drawingManager.value;
  if (!dm) return;

  const point = eventToPoint(e);

  // Anchor drag: update geometry live.
  if (dm.isDraggingAnchor && editingAnnotationId.value && currentAnchor) {
    const idx = annotations.value.findIndex(
      (a) => a.id === editingAnnotationId.value
    );
    if (idx === -1) return;

    const anchorKey = currentAnchor.key ?? currentAnchor.index;
    const updated = dm.updateAnnotationFromAnchor(
      annotations.value[idx],
      anchorKey,
      point
    );
    annotations.value[idx] = updated;
    renderAllAnnotations();
    return;
  }

  // Whole-annotation move: translate geometry live.
  if (dm.isMovingAnnotation && editingAnnotationId.value) {
    const idx = annotations.value.findIndex(
      (a) => a.id === editingAnnotationId.value
    );
    if (idx === -1) return;
    annotations.value[idx] = dm.moveAnnotationTo(point);
    renderAllAnnotations();
    return;
  }

  // Drawing preview.
  if (dm.isDrawing) {
    dm.continueDrawing(point);
  }
}

function handleMouseUp(e) {
  const dm = drawingManager.value;
  if (!dm) return;

  detachDragListeners();
  const point = eventToPoint(e);

  // Finish anchor drag.
  if (dm.isDraggingAnchor) {
    dm.isDraggingAnchor = false;
    currentAnchor = null;
    dm.clearFreehandVertexDrag();
    pushHistory(editingAnnotationId.value);
    renderAllAnnotations();
    return;
  }

  // Finish whole-annotation move.
  if (dm.isMovingAnnotation) {
    dm.endMove();
    pushHistory(editingAnnotationId.value);
    renderAllAnnotations();
    return;
  }

  // Finish drawing a new annotation.
  if (dm.isDrawing) {
    const annotation = dm.finishDrawing(point);
    if (annotation) {
      addNewAnnotation(annotation);
    } else {
      // Drawing was too small / cancelled; restore the full render.
      renderAllAnnotations();
    }
  }
}

function attachDragListeners() {
  window.addEventListener("mousemove", handleMouseMove);
  window.addEventListener("mouseup", handleMouseUp);
}

function detachDragListeners() {
  window.removeEventListener("mousemove", handleMouseMove);
  window.removeEventListener("mouseup", handleMouseUp);
}

// Hover-only handler (canvas-level) for cursor feedback. The actual dragging is
// driven by the window-level listeners attached in handleMouseDown.
function handleHover(e) {
  const canvas = canvasRef.value;
  const dm = drawingManager.value;
  if (!canvas || !dm) return;
  if (dm.isDraggingAnchor || dm.isDrawing || dm.isMovingAnnotation) return;

  if (isAdding.value) {
    canvas.style.cursor = canAdd.value ? "crosshair" : "default";
    return;
  }

  if (!editingAnnotationId.value) {
    canvas.style.cursor = "default";
    return;
  }

  const point = eventToPoint(e);
  const annotation = annotations.value.find(
    (a) => a.id === editingAnnotationId.value
  );
  if (!annotation) {
    canvas.style.cursor = "default";
    return;
  }

  const anchor = dm.getAnchorAtPoint(point, annotation);
  if (anchor) {
    canvas.style.cursor = anchor.cursor || "pointer";
    // For freehand, reveal the hovered vertex (handles are otherwise hidden).
    if (annotation.type === "freehand") {
      if (highlightedAnchorIndex.value !== anchor.index) {
        highlightedAnchorIndex.value = anchor.index;
        renderAllAnnotations();
      }
    }
  } else if (dm.isPointInAnnotation(point, annotation)) {
    canvas.style.cursor = "move";
    clearFreehandHighlight();
  } else {
    canvas.style.cursor = "default";
    clearFreehandHighlight();
  }
}

// Clear the hovered freehand vertex and redraw if it was showing.
function clearFreehandHighlight() {
  if (highlightedAnchorIndex.value !== null) {
    highlightedAnchorIndex.value = null;
    renderAllAnnotations();
  }
}

function handleMouseLeave() {
  clearFreehandHighlight();
}

// Remove a freehand vertex by index (Alt/Shift-click on a revealed vertex).
function deleteFreehandVertex(annotation, index) {
  if (
    !annotation.freehandpoints ||
    annotation.freehandpoints.length <= 3
  )
    return;
  const idx = annotations.value.findIndex((a) => a.id === annotation.id);
  if (idx === -1) return;
  const newPoints = annotation.freehandpoints.filter((_, i) => i !== index);
  annotations.value[idx] = { ...annotation, freehandpoints: newPoints };
  highlightedAnchorIndex.value = null;
  pushHistory(annotation.id);
  renderAllAnnotations();
}

// Double-click a freehand edge to insert a new vertex there. Ignored when the
// click is within the hit radius of an existing vertex (grab that instead).
function handleDoubleClick(e) {
  const dm = drawingManager.value;
  if (!dm || !editingAnnotationId.value) return;
  const annotation = editedAnnotation.value;
  if (!annotation || annotation.type !== "freehand") return;

  const point = eventToPoint(e);
  if (dm.getAnchorAtPoint(point, annotation)) return; // vertex nearby -> skip

  const res = dm.getFreehandInsertAt(annotation, point);
  if (!res) return;

  const idx = annotations.value.findIndex((a) => a.id === annotation.id);
  if (idx === -1) return;
  const newPoints = [...annotation.freehandpoints];
  newPoints.splice(res.insertIndex, 0, res.originalPoint);
  annotations.value[idx] = { ...annotation, freehandpoints: newPoints };
  highlightedAnchorIndex.value = res.insertIndex;
  pushHistory(annotation.id);
  renderAllAnnotations();
}

// ---- Annotation management ----
function addNewAnnotation(annotation) {
  const pathology = selectedPathology.value;
  if (!pathology) {
    renderAllAnnotations();
    return;
  }

  const isOther = isOtherSelected.value;
  const typedName = otherName.value.trim();
  const name = isOther ? typedName : pathology.label;
  if (!name) {
    renderAllAnnotations();
    return;
  }

  annotation.id = `new_${Date.now()}`;
  annotation.pathology = name; // display name
  // For "Other", store the typed name verbatim (matches reference behaviour);
  // otherwise store the normalized lowercase label as predicted_diagnosis.
  annotation.value = isOther ? name : pathology.value;
  annotation.color = pathology.color;
  annotation.isOriginal = false;
  annotation.pathologyId = pathology.id;

  annotations.value.push(annotation);
  // Adding a finding isn't an edit to "undo while focused on it" — tag as a
  // barrier so focusing it later and undoing stops here rather than removing it.
  pushHistory(null);

  // Return to view mode after adding. Keep the pathology selection so multiple
  // additions of the same type are convenient, but clear any typed "Other" name.
  editorMode.value = "view";
  if (isOther) {
    otherName.value = "";
  }

  renderAllAnnotations();
}

function toggleEdit(id) {
  // Diagnosis detections have no coordinates and cannot be edited.
  const target = annotations.value.find((a) => a.id === id);
  if (target?.isDiagnosis) return;

  if (editingAnnotationId.value === id) {
    // Commit (check): exit focus mode and keep the edits made this session.
    editingAnnotationId.value = null;
    editSessionSnapshot.value = null;
    highlightedAnchorIndex.value = null;
  } else {
    editingAnnotationId.value = id;
    editorMode.value = "view";
    highlightedAnchorIndex.value = null;
    // Snapshot the finding's geometry so the per-finding "Cancel" (X) can revert
    // only this session's edits back to where they were on focus-enter.
    editSessionSnapshot.value = cloneAnnotationGeometry(target);
  }
  renderAllAnnotations();
}

function cloneAnnotationGeometry(ann) {
  if (!ann) return null;
  return {
    id: ann.id,
    type: ann.type,
    coords: ann.coords ? { ...ann.coords } : undefined,
    freehandpoints: ann.freehandpoints
      ? ann.freehandpoints.map((p) => ({ ...p }))
      : undefined,
  };
}

// Per-finding "Cancel" (X): revert this finding's geometry to its focus-enter
// snapshot and exit focus mode. The revert is recorded as one undoable history
// entry (a no-op if nothing actually changed, so the dirty flag stays clean).
function cancelEditSession(id) {
  const snap = editSessionSnapshot.value;
  if (snap && snap.id === id) {
    const idx = annotations.value.findIndex((a) => a.id === id);
    if (idx !== -1) {
      const current = annotations.value[idx];
      const reverted = { ...current };
      if (snap.coords) reverted.coords = { ...snap.coords };
      if (snap.freehandpoints)
        reverted.freehandpoints = snap.freehandpoints.map((p) => ({ ...p }));
      annotations.value[idx] = reverted;
      pushHistory(id);
    }
  }
  editingAnnotationId.value = null;
  editSessionSnapshot.value = null;
  highlightedAnchorIndex.value = null;
  renderAllAnnotations();
}

function deleteDetection(id) {
  const idx = annotations.value.findIndex((a) => a.id === id);
  if (idx === -1) return;

  const removed = annotations.value[idx];
  annotations.value.splice(idx, 1);

  if (removed.isOriginal) {
    deletedIds.value.add(id);
  }

  if (editingAnnotationId.value === id) {
    editingAnnotationId.value = null;
  }

  // A delete is a barrier: tag null so focus-mode undo never crosses it.
  pushHistory(null);
  renderAllAnnotations();
}

// ---- Save payload ----
// Builds the pathologies array for the `update` API. Includes Cardio/Thoracic
// unchanged (passthrough), surviving edited originals, and newly added ones.
function buildUpdatePathologies() {
  const result = [...cardioThoracic.value];

  annotations.value.forEach((ann) => {
    if (deletedIds.value.has(ann.id)) return;

    if (ann.isOriginal) {
      const pathology = { ...ann.originalAi };
      if (ann.isDiagnosis) {
        // Diagnosis detections have no coordinates; pass through unchanged.
      } else if (ann.type === "box") {
        pathology.bounding_box = ann.coords;
      } else {
        pathology.freehandpoints = ann.freehandpoints;
      }
      result.push(pathology);
    } else {
      result.push({
        bounding_box: ann.type === "box" ? ann.coords : undefined,
        freehandpoints:
          ann.type === "freehand" ? ann.freehandpoints : undefined,
        predicted_diagnosis: ann.value,
        confidence: null,
        comment: null,
      });
    }
  });

  return result;
}

function handleSave() {
  isSaving.value = true;
  emit("save", {
    updatePathologies: buildUpdatePathologies(),
    deletedIds: Array.from(deletedIds.value),
    originalIds: [...allOriginalIds.value],
    summary: {
      added: annotations.value.filter((a) => !a.isOriginal).length,
      deleted: deletedIds.value.size,
      totalAfter: annotations.value.length,
    },
  });
}

function handleCancel() {
  // Always confirm before discarding unsaved edits.
  if (auditChanged.value) {
    showCancelWarning.value = true;
    return;
  }
  emit("cancel");
}

function confirmCancel() {
  emit("cancel");
}

// ---- Keyboard shortcuts ----
// Undo/redo are ignored while focus is in a text field (e.g. the "Other"
// pathology name input) so typing isn't hijacked. Esc walks a priority chain:
// exit the focus-edit sub-mode -> cancel an in-progress add -> prompt to close.
function onKeyDown(e) {
  const tag = (e.target?.tagName || "").toLowerCase();
  if (tag === "input" || tag === "select" || tag === "textarea") return;

  const mod = e.ctrlKey || e.metaKey;
  const key = (e.key || "").toLowerCase();

  if (mod && key === "z") {
    e.preventDefault();
    if (e.shiftKey) redo();
    else undo();
    return;
  }
  if (mod && key === "y") {
    e.preventDefault();
    redo();
    return;
  }
  if (key === "escape") {
    handleEscape();
  }
}

function handleEscape() {
  if (editingAnnotationId.value) {
    // Exit focus mode, keeping the edits made this session (commit-on-dismiss).
    editingAnnotationId.value = null;
    editSessionSnapshot.value = null;
    renderAllAnnotations();
    return;
  }
  if (isAdding.value) {
    setMode("view");
    return;
  }
  handleCancel();
}

// Allow parent to signal save completion (success or error), and to query
// whether there are unsaved edits (used to guard navigation).
defineExpose({
  saveDone() {
    isSaving.value = false;
  },
  isDirty() {
    return auditChanged.value;
  },
});

// ---- Reactivity ----
watch(
  () => props.aiPathologies,
  () => {
    buildAnnotations();
    nextTick(() => renderAllAnnotations());
  },
  { immediate: true, deep: false }
);

watch(
  () => props.imageSrc,
  () => {
    imageReady.value = false;
    drawingManager.value = null;
    if (resizeObserver.value) {
      resizeObserver.value.disconnect();
      resizeObserver.value = null;
    }
  }
);

// Re-render when the label visibility toggle changes so the editor stays in
// sync with the read-only overlay's "Show/Hide Detection Labels" control.
watch(
  () => props.showLabels,
  () => {
    renderAllAnnotations();
  }
);

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeyDown);
  detachDragListeners();
  endHintDrag();
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }
});
</script>

<style scoped>
.prospective-ai-editor {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg-secondary);
  z-index: 100;
}

.editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1rem;
  background-color: var(--color-bg-primary);
  border-bottom: 1px solid var(--color-border-primary);
  flex-shrink: 0;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-text-primary);
}

.title-icon {
  color: var(--color-accent-primary, #ffc107);
}

.change-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: var(--color-accent-primary, #ffc107);
  padding: 0.2rem 0.6rem;
  border-radius: var(--border-radius-small);
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border-primary);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.header-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.15s ease;
}

.header-btn:hover:not(:disabled) {
  border-color: var(--color-border-hover);
  color: var(--color-accent-primary, #ffc107);
}

.header-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.editor-body {
  flex: 1;
  display: flex;
  min-height: 0;
}

.canvas-stage {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  min-width: 0;
  min-height: 0;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.editor-image {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
}

.editor-canvas {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: crosshair;
}

.no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  gap: 1rem;
}

.canvas-hint {
  position: absolute;
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
  padding: 0.4rem 0.5rem 0.4rem 0.4rem;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border-primary);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  font-size: 0.8rem;
  max-width: 22rem;
  cursor: move;
  z-index: 5;
}

.canvas-hint .hint-drag {
  flex-shrink: 0;
  color: var(--color-text-secondary);
  padding: 0.15rem 0.25rem;
  font-size: 0.75rem;
  line-height: 1;
}

.canvas-hint .hint-close {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  padding: 0.15rem 0.25rem;
  font-size: 0.75rem;
  line-height: 1;
  cursor: pointer;
}

.canvas-hint .hint-close:hover {
  color: var(--color-text-primary);
}

.canvas-hint .hint-text {
  flex: 1;
  line-height: 1.35;
}

.detections-panel {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg-primary);
  border-left: 1px solid var(--color-border-primary);
}

.panel-section {
  padding: 0.75rem 1rem;
}

.detections-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 0.5rem;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.panel-header h4 {
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-text-primary);
}

.deleted-count {
  font-size: 0.7rem;
  color: var(--color-danger, #dc3545);
  background-color: var(--color-bg-secondary);
  padding: 0.15rem 0.4rem;
  border-radius: var(--border-radius-small);
}

.panel-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding-right: 0.15rem;
}

.empty-list {
  margin: 1rem 0;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  font-style: italic;
  text-align: center;
}

.add-section {
  border-top: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-secondary);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-accent-primary, #ffc107);
  background-color: var(--color-accent-primary, #ffc107);
  color: var(--color-bg-primary, #0c2030);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.15s ease;
}

.add-btn:hover:not(:disabled) {
  filter: brightness(1.05);
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-btn.active {
  background-color: var(--color-danger, #dc3545);
  border-color: var(--color-danger, #dc3545);
  color: #fff;
}

.draw-type-badge {
  margin-left: auto;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.1rem 0.4rem;
  border-radius: var(--border-radius-small);
  background-color: rgba(0, 0, 0, 0.15);
}

.action-section {
  border-top: 1px solid var(--color-border-primary);
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 0.85rem;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.15s ease;
}

.action-btn:hover:not(:disabled) {
  border-color: var(--color-border-hover);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-btn {
  background-color: var(--color-success, #28a745);
  color: #fff;
  border-color: var(--color-success, #28a745);
}

.save-btn:hover:not(:disabled) {
  filter: brightness(1.08);
}

.cancel-btn:hover:not(:disabled) {
  background-color: var(--color-danger, #dc3545);
  color: #fff;
  border-color: var(--color-danger, #dc3545);
}

@media (max-width: 900px) {
  .editor-body {
    flex-direction: column;
  }
  .detections-panel {
    width: 100%;
    height: 280px;
    border-left: none;
    border-top: 1px solid var(--color-border-primary);
  }
}
</style>
